import type { CupasQuery } from "generated-graphql/graphql";

import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Box, Button, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useCallback, useMemo, useState } from "react";

import { ConfirmDeleteCupa } from "./ConfirmDeleteCupa";
import { CupaDialog } from "./CupaDialog";
import { GET_CUPAS } from "./schema";

type Row = CupasQuery["cupas"]["items"][number];

// ════════════════════════════════════════════════════════════════════════════
// MODULE LEVEL - Static config (stable references, zero runtime overhead)
// ════════════════════════════════════════════════════════════════════════════

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "cupaCode", sort: "asc" }];
const FILTER_GROUPS: FilterGroup[] = [
  { name: "CUPA Code", fields: ["cupaCode"] },
  { name: "Name", fields: ["displayName", "districtName"] },
  { name: "County", fields: ["county"] },
];

const getItems = (data: CupasQuery) => data.cupas.items ?? [];
const getCount = (data: CupasQuery) => data.cupas.count ?? 0;

// ════════════════════════════════════════════════════════════════════════════
// COMPONENT - Only dynamic stuff here
// ════════════════════════════════════════════════════════════════════════════

export function Cupas() {
  const { selectedId: cupaId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("cupaId");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [confirmDeleteCupa, setConfirmDeleteCupa] = useState<Row | null>(null);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      { field: "cupaCode", headerName: "CUPA Code", flex: 1 },
      { field: "displayName", headerName: "Display Name", flex: 4 },
      { field: "county", headerName: "County", flex: 2 },
      { field: "districtName", headerName: "District Name", flex: 4 },
      {
        field: "actions",
        type: "actions",
        align: "left",
        getActions: ({ row }) => [
          <Tooltip title="Edit CUPA" key="edit">
            <GridActionsCellItem
              label="Edit CUPA"
              onClick={() => pushIdToUrl(row.id)}
              icon={<Edit />}
            />
          </Tooltip>,
          <Tooltip title="Delete CUPA" key="delete">
            <GridActionsCellItem
              label="Delete CUPA"
              onClick={() => setConfirmDeleteCupa(row)}
              icon={<Delete />}
            />
          </Tooltip>,
        ],
      },
    ],
    [pushIdToUrl]
  );

  const commandButtons = useMemo(
    () => [
      <Button key="add-cupa" variant="contained" onClick={() => setAddDialogOpen(true)}>
        Add CUPA
      </Button>,
    ],
    []
  );

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search: omnisearch || undefined,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    []
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<Row>) => pushIdToUrl(params.row.id),
    [pushIdToUrl]
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGridV2
        query={GET_CUPAS}
        getItems={getItems}
        getCount={getCount}
        persistenceKey="staff-cupas-v2"
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialSortModel={INITIAL_SORT_MODEL}
        filterGroups={FILTER_GROUPS}
        columns={columns}
        commandButtons={commandButtons}
        buildVariables={buildVariables}
        onRowClick={handleRowClick}
        noRowsMessage="No CUPAs found."
      />
      {cupaId && <CupaDialog onClose={popIdFromUrl} mode="edit" cupaId={cupaId} />}
      {addDialogOpen && <CupaDialog onClose={() => setAddDialogOpen(false)} mode="add" />}
      {confirmDeleteCupa !== null && (
        <ConfirmDeleteCupa
          cupaId={confirmDeleteCupa.id}
          onClose={() => setConfirmDeleteCupa(null)}
        />
      )}
    </Box>
  );
}
